import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AuthenticationService } from '@core/authentication.service';
import { MasqueradeService } from '@core/services/masquerade/masquerade.service';
import { UserService } from '@core/user.service';
import { ApiResponse } from '@models/api-response';
import { KeyValuePair } from '@models/key-value-pair';
import { PagedResponse } from '@models/paged-response';
import { School } from '@models/school';
import { IUser } from '@models/user';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { LicenseAssignmentType } from '@shared/enums/license-status';
import { LoginStatus } from '@shared/enums/login-status';
import { MasqueradeAbilities } from '@shared/enums/masquerade-abilities';
import { RoleType } from '@shared/enums/role-type';
import { Helpers } from '@shared/helpers';
import { BrandedEditUserComponent } from '@shared/manage-users/branded-edit-user/branded-edit-user.component';
import { GenericConfirmModalComponent } from '@shared/modals/generic-confirm-modal/generic-confirm-modal.component';
import { BrandedUserSettingsComponent } from '@shared/user-settings/branded-user-settings.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { cloneDeep } from 'lodash';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, from, mergeMap, of, skipWhile, Subscription, throwError } from 'rxjs';
import {
  BrandedLicenseModalComponent
} from 'src/app/schools/school/license-modal/branded-license-modal/branded-license-modal.component';
import { SchoolsFacade } from 'src/app/schools/store/schools.facade';
import PageChangeEvent = ZbpDataTable.PageChangeEvent;

@Component({
  selector: 'zbp-branded-user-management-table',
  templateUrl: './branded-user-management-table.component.html',
  styleUrls: ['./branded-user-management-table.component.scss'],
  standalone: false
})
export class BrandedUserManagementTableComponent implements OnInit, OnDestroy {

  readonly NO_SCHOOL_DELETE_TOOLTIP: string = `You must select a specific school in your district before deleting a user.`;

  hasSearched = false;
  isSearching = true;
  isOneRoster: boolean = false;
  isClever: boolean = false;
  isDistSearch: boolean = false;
  userIsDistrictAdmin: boolean = false;
  userIsSchoolAdmin: boolean = false;
  exportFileName: string;
  role: RoleType;
  roleName: string;
  pluralName: string;
  htmlIdName: string;
  schoolId: string = null;
  hasSchoolIdSet: boolean = false;
  districtId: string = null;
  educationalUnitId: string = null;
  school: School = null;
  isSchoolWithinDistrict: boolean = false;
  schools: School[] = [];
  userSearchCriteria: string = null;
  page: number = 1;
  pageSize: number = 25;
  totalResults: number = 0;
  users: IUser[] = [];
  confirmList = [
    'They will retain any other roles held. If they have no other roles, their account will be deleted permanently.',
    'Teacher licenses assigned to this user will be revoked.',
  ];
  modalRef: NgbModalRef;
  isHighlightsPortalUser: boolean = false;
  subscriptions: Subscription[] = [];
  masqueradeAbilities = MasqueradeAbilities;
  distWideRemoveUserTooltipText = 'You must select a specific school in your district before deleting a user.';
  showExternalId = false;
  showUserName = false;
  showEmail = false;
  showLicenses = false;
  showMasqButton = false;
  showEmailButton = false;
  orderByParam: string = '';
  usersSubject: BehaviorSubject<IUser[]> = new BehaviorSubject<IUser[]>([]);
  protected readonly iconNames = zbpIconNames;
  private lastFocusedUserId: string | null = null;

  @ViewChild('deleteModalBody') modalContent!: TemplateRef<any>;

  constructor(
    public router: Router,
    public route: ActivatedRoute,
    public authService: AuthenticationService,
    public userService: UserService,
    public schoolsFacade: SchoolsFacade,
    public modalService: NgbModal,
    public toastr: ToastrService,
    public masqueradeService: MasqueradeService,
    public liveAnnouncer: LiveAnnouncer,
  ) {
  }

  ngOnInit(): void {
    this.page = 1;
    this.totalResults = 0;
    this.isSearching = true;

    this.subscriptions.push(
      this.schoolsFacade.schools$
        .subscribe((schools) => {
          this.schools = schools;
        }),
      this.route.queryParams
        .pipe(
          skipWhile(params => !params),
          mergeMap((params: Params) => {
            if (params && (params.schoolId || params.districtId)) {
              this.isDistSearch = false;
              if (params.educationalUnitId === '00000000-0000-0000-0000-000000000000') {
                this.isDistSearch = true;
              }

              this.userSearchCriteria = params.userSearchCriteria || null;
              this.schoolId = params.schoolId;
              this.school = this.schools.find(school => school.schoolId === params.schoolId);
              this.isSchoolWithinDistrict = !!this.school?.districtId;
              this.hasSchoolIdSet = !!this.school?.schoolId && !!this.school?.abbreviatedSchoolId;
              this.districtId = params.districtId;
              this.educationalUnitId = params.educationalUnitId;
              this.userIsDistrictAdmin = this.userService.user.isDistrictAdmin;
              this.userIsSchoolAdmin = this.userService.user.isSchoolAdmin;
              this.page = params.page || 1;

              if (params.isOneRoster) {
                if (params.isOneRoster.toLowerCase() === 'true') {
                  this.isOneRoster = true;
                } else {
                  this.isOneRoster = false;
                }
              } else {
                this.isOneRoster = this.school?.isOneRoster;
              }

              this.isSearching = true;
              return this.userService.searchUsers(this.getSearchParams());
            }
            return of(null);
          }),
          catchError((err) => {
            console.error(err);
            this.toastr.error('An error occurred on this page. Please try again.');
            return of(null);
          })
        )
        .subscribe((res: PagedResponse<IUser[]>) => {
          if (res) {
            this.handleUserSearchResponse(res);
          }
        }),
      // Subscribe to any incoming users from the API and change the table based on that.
      this.usersSubject.subscribe((users: IUser[]) => {
        this.users = users;
      }, (err: any[]) => {
        console.error(err);
        this.toastr.error('An error occurred loading users.');
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  getNoResultsMessage(): string {
    return this.userSearchCriteria
      ? 'We couldn\'t find any users based on your search. Please try a different search criteria.'
      : `You have no ${this.pluralName?.toLocaleLowerCase()} in your school. Please add or import a user.`;
  }

  onOrderByParamChange(newOrderByParam: string): void {
    this.orderByParam = newOrderByParam;
    this.searchUsers();
  }

  searchUsers() {
    this.isSearching = true;
    this.userService.searchUsers(this.getSearchParams())
      .subscribe((res: PagedResponse<IUser[]>) => {
        if (res) {
          this.handleUserSearchResponse(res);
        }
      });
  }

  handleUserSearchResponse(res: PagedResponse<IUser[]>) {
    this.hasSearched = true;
    if (res.success) {
      this.totalResults = res.totalRecords;
      this.page = res.pageNumber;
      const users = res.response.reduce((result: IUser[], user: IUser) => {
        const schools = user.schools.filter(s => s.roleTypes?.includes(this.role));
        // Update the schools list based on the role type
        if (schools.length === user.schools.length) {
          result.push(user);
        } else {
          const newUser = cloneDeep(user);
          newUser.schools = schools;
          result.push(newUser);
        }
        return result;
      }, []);
      if (users.length === 0 && this.userSearchCriteria) {
        this.liveAnnouncer.announce(`No results displayed for search criteria: ${this.userSearchCriteria}`, 'polite');
      } else {
        this.liveAnnouncer.announce(`${users.length} results displayed`, 'polite');
      }
      this.usersSubject.next(users);
    } else {
      this.usersSubject.error(res.messages);
    }

    this.isSearching = false;
  }

  get hasResults(): boolean {
    return this.hasSearched && this.users.length > 0;
  }

  pageChange(event: PageChangeEvent): void {
    const { page } = event;
    const queryParams: Params = {
      userSearchCriteria: this.userSearchCriteria,
      schoolId: this.schoolId,
      page: Helpers.sanitizePageNumber(page),
    };
    this.router.navigate([], { relativeTo: this.route, queryParams, queryParamsHandling: 'merge' });
    // Scrolls to the top of the page.
    $(window).scrollTop(0);
  }


  editUser(user: IUser): void {
    this.lastFocusedUserId = user.userId;

    this.modalRef = BrandedEditUserComponent.open(this.modalService, {
      currentSchool: this.school,
      roleToEdit: this.role,
      userToEditOriginal: user,
      showParentFieldsSearch: this.role === RoleType.Student,
      options: { size: 'lg' },
    });

    this.subscriptions.push(
      from(this.modalRef.result)
        .pipe(
          catchError((err) => {
            if (err instanceof Error) {
              return throwError(() => err);
            }
            return of(null);
          })
        )
        .subscribe((changed?: IUser) => {
          if (changed) {
            const index = this.users.findIndex(s => s.userId === changed.userId);
            if (index !== -1) {
              if (changed['refreshTable']) {
                this.searchUsers();
              } else {
                const users = [...this.users];
                // Updates the user in the collection.
                users[index] = changed;
                this.usersSubject.next(users);
              }
            }
          }
        })
    );
    // Restore focus to the button when modal closes
    this.modalRef.result.finally(() => {
      setTimeout(() => this.restoreFocus(), 0);
    });

  }

  deleteUser(user: IUser): void {
    this.modalRef = GenericConfirmModalComponent.open(this.modalService, {
      title: `REMOVE ${this.roleName.toUpperCase()} USER - ${user.fullName}`,
      confirm: { label: 'Remove' },
      options: { size: 'lg' },
      bodyRef: this.modalContent
    });

    this.subscriptions.push(
      from(this.modalRef.result)
        .pipe(
          catchError(() => of(false)),
          mergeMap((confirm: boolean) => (confirm
            ? this.userService.removeUserRole(user.userId, this.role, false, this.schoolId)
            : of(null)))
        )
        .subscribe((res?: ApiResponse<boolean>) => {
          if (res) {
            if (res.success) {
              this.toastr.success(`${user.fullName} successfully removed as ${this.role}.`);
              this.totalResults--;
              if (this.totalResults % this.pageSize === 0 && this.page > 1) {
                // deleleted all users on the page and need to move back a page unless we're on page 1
                this.page--;
              }
              this.searchUsers();
            } else if (res.messages) {
              this.toastr.error(res.messages[0]);
            } else {
              this.toastr.error(`An error occurred removing ${user.fullName} as a ${this.role}.`);
            }
          }
        })
    );
  }

  findSchool(schoolId: string): School {
    return this.schools?.find(s => schoolId === s.educationalUnitId);
  }

  get canChangePassword(): boolean {
    return this.userService.user.profileDetail?.canChangeStudentPassword;
  }

  private getSearchParams(): KeyValuePair[] {
    const params: KeyValuePair[] = [
      { key: 'pageNumber', value: this.page },
      { key: 'pageSize', value: this.pageSize },
      { key: 'activeOnly', value: true },
      { key: 'roleTypes', value: this.role },
    ];

    if (this.schoolId) {
      params.push({ key: 'educationalUnitId', value: this.schoolId ?? this.educationalUnitId });
    }

    if (this.districtId) {
      params.push({ key: 'districtId', value: this.districtId });
    }

    if (this.role === RoleType.Student
      || this.role === RoleType.DistrictAdministrator
      || this.role === RoleType.SchoolAdministrator) {
      params.push({ key: 'excludeLicenses', value: true });
    }

    if (this.userSearchCriteria) {
      params.push({ key: 'userSearchCriteria', value: this.userSearchCriteria });
    }

    if (this.orderByParam.length > 0) {
      params.push({ key: 'orderBy', value: this.orderByParam });
    }

    return params;
  }

  resendUserEmail(user: IUser) {
    this.authService.resendUserEmail(user.userId)
      .subscribe((res) => {
        if (res.success) {
          if (user.loginStatus === LoginStatus.NotSetup) {
            this.toastr.success('Successfully sent activation email.');
          } else {
            this.toastr.success('Successfully sent password reset email.');
          }
        } else {
          this.toastr.error(res.messages[0]);
        }
      });
  }

  getSchoolNames(user) {
    let schoolList = '';
    const userSchoolList = [];

    user.schools.forEach((membership) => {
      if (!userSchoolList.includes(membership.name)) {
        userSchoolList.push(membership.name);
        schoolList += `<li>${membership.name.toLowerCase()
          .split(' ')
          .map(s => s.charAt(0).toLocaleUpperCase() + s.substring(1))
          .join(' ')
        }</li>`;
      }
    });

    return schoolList;
  }

  editLicenses(index: number): void {
    this.modalRef = BrandedLicenseModalComponent.open(this.modalService, {
      school: this.findSchool(this.schoolId),
      assignedId: this.users[index].userId,
      assignmentType: LicenseAssignmentType.Teacher,
    });
    this.subscriptions.push(
      from(this.modalRef.result)
        .pipe(
          catchError((err) => {
            if (err instanceof Error) {
              return throwError(() => err);
            }
            return of(null);
          })
        )
        .subscribe((newCount?: number) => {
          if (newCount !== null && newCount !== undefined) {
            this.users[index].assignedLicenseCount = newCount;
            this.toastr.success('Successfully updated the user licenses.');
          }
        })
    );
  }

  canLoginAs(user: IUser): MasqueradeAbilities {
    return this.masqueradeService.getMasqueradeAbility(user);
  }

  verifyMasqueradeRequestModal(email: string) {
    const masqueradeModalRefSub = this.masqueradeService.confirmLoginAsModal(email).subscribe();
    this.subscriptions.push(masqueradeModalRefSub);
  }

  /**
   * Opens user settings modal for either District-wide settings or School-wide settings based on isDistSearch.
   */
  openUserSettings(isDistrictwide: boolean) {
    this.modalRef = this.modalService.open(BrandedUserSettingsComponent, { size: 'lg' });
    this.modalRef.componentInstance.schoolId = isDistrictwide ? this.districtId : this.schoolId;
    this.modalRef.componentInstance.isDistrictWide = isDistrictwide;
    this.modalRef.componentInstance.isSchoolUnderDistrict = this.isSchoolWithinDistrict;
    // Disable form when loading Schoolwide Settings for a school within a district.
    this.modalRef.componentInstance.formDisabled = !isDistrictwide && this.isSchoolWithinDistrict;

    // BrandedUserSettingsComponent handles toast notifications, modal will not auto-close if save throws any errors
    this.subscriptions.push(
      from(this.modalRef.result)
        .pipe(
          catchError(() => of(null))
        )
        .subscribe(),
    );
  }

  buildActions = (user: IUser) => {
    const newActions = [];
    if (this.showMasqButton) {
      newActions.push({
        iconName: zbpIconNames.mask,
        tooltip: `Masquerade as ${user.fullName}`,
        ariaLabel: `Masquerade as ${user.fullName}`,
        onClick: () => this.verifyMasqueradeRequestModal(user.username),
      });
    }

    newActions.push({
      iconName: zbpIconNames.pencil,
      tooltip: `Manage ${user.fullName}`,
      ariaLabel: `Manage ${user.fullName}`,
      internalId: this.getManageButtonId(user.userId),
      onClick: () => this.editUser(user),
      disabled: this.isOneRoster
        && (this.role === RoleType.DistrictAdministrator || this.role === RoleType.SchoolAdministrator),
    });

    if (this.showEmailButton) {
      newActions.push({
        iconName: zbpIconNames.envelope,
        tooltip: `Resend email to ${user.fullName}`,
        ariaLabel: `Resend email to ${user.fullName}`,
        onClick: () => this.resendUserEmail(user),
      });
    }

    if (!this.isOneRoster || this.role === RoleType.Parent) {
      newActions.push({
        iconName: zbpIconNames.x_cross_circle,
        tooltip: this.getDeleteUserTooltip(user),
        ariaLabel: this.getDeleteUserTooltip(user),
        disabled: !this.hasSchoolId(),
        onClick: () => this.deleteUser(user),
      });
    }

    return newActions;
  };

  getDeleteUserTooltip(user: IUser): string {
    return this.hasSchoolId()
      ? `Remove ${user.fullName}`
      : this.NO_SCHOOL_DELETE_TOOLTIP;
  }

  hasSchoolId(): boolean {
    return !Helpers.isNullOrUndefined(this.schoolId);
  }

  private getManageButtonId(userId: string): string {
    return `manage-user-${userId}`;
  }

  private restoreFocus(): void {
    if (this.lastFocusedUserId) {
      const target = document.getElementById(this.getManageButtonId(this.lastFocusedUserId));
      target?.focus();
    }
  }

}
